import type { NodeDetailResponse } from '../types/triage';

const API_BASE = import.meta.env.VITE_API_URL?.trim() || 'http://localhost:3002';

let getAuthToken: (() => Promise<string | null>) | null = null;
let onSessionExpired: (() => void) | null = null;
// Resolves when setAuthTokenProvider has been called. React runs child
// useEffects before parent useEffects, so the first wave of API requests
// from page-level effects (loadLocations, loadTickets, loadIssues) used
// to fire BEFORE App.tsx's mount effect could install the provider,
// producing a burst of header-less requests that the server correctly
// 401'd. authHeaders() now awaits this gate.
let authReadyResolve: () => void;
const authReady = new Promise<void>((resolve) => {
  authReadyResolve = resolve;
});

export function setAuthTokenProvider(provider: () => Promise<string | null>) {
  getAuthToken = provider;
  authReadyResolve();
}

export function setSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler;
}

async function authHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  // Wait up to 2s for the provider to be installed by App.tsx's mount
  // effect. Without this gate the first batch of requests from page-level
  // effects raced the parent's setAuthTokenProvider call and went out
  // unauthenticated.
  await Promise.race([
    authReady,
    new Promise<void>((resolve) => setTimeout(resolve, 2000)),
  ]);
  if (getAuthToken) {
    // Let the provider throw on login-required (App.tsx kicks off the
    // redirect). We don't catch here — propagating the error aborts the
    // request before it hits the server, which prevents the spurious 401
    // -> forced-logout cascade we used to see when null tokens leaked
    // through.
    const token = await getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Error from the API layer that preserves HTTP status + the server's `detail`
 * payload alongside the human message. Call sites can switch on `.status` for
 * 403/404 etc. without parsing the error text.
 */
export class ApiError extends Error {
  status: number;
  detail?: string;
  constructor(message: string, status: number, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401 && !retried) {
    // One-shot retry. Silent token refresh can race a request — the first
    // attempt may have used a token that the server has already rotated.
    // Pause briefly so a fresh token can settle, then retry. Only after the
    // SECOND 401 do we conclude the session is dead.
    await new Promise((resolve) => setTimeout(resolve, 250));
    return request<T>(path, init, true);
  }
  if (res.status === 401) {
    if (onSessionExpired) onSessionExpired();
    throw new ApiError('Session expired. Redirecting to login...', 401);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { error?: string; detail?: string }));
    // 403 has a friendly default; everything else keeps the server's error
    // string when present so toasts surface real reasons. detail is carried
    // separately so call sites can choose to show it.
    const fallback = res.status === 403
      ? "You don't have permission to do that."
      : `API error ${res.status}`;
    throw new ApiError(body.error || fallback, res.status, body.detail);
  }
  return res.json();
}

// --- Tickets ---

// Current user profile + role flags. is_admin is the server's computed
// view of the JWT roles claim against ADMIN_ROLES env — single source of
// truth, the FE never decodes the JWT itself. Used to gate Agent Health
// + Reclassify-all UI surfaces (server still re-checks roles on every
// privileged endpoint via requireAdmin).
export interface CurrentUser {
  sub: string;
  email: string | null;
  name: string | null;
  roles: string[];
  is_admin: boolean;
}

export function fetchCurrentUser() {
  return request<CurrentUser>('/api/me');
}

export interface TicketListParams {
  location_id?: string;
  class_ids?: string;
  status?: string;
  sort?: string;
  limit?: number;
  offset?: number;
}

export function fetchTickets(params: TicketListParams = {}) {
  const qs = new URLSearchParams();
  if (params.location_id) qs.set('location_id', params.location_id);
  if (params.class_ids) qs.set('class_ids', params.class_ids);
  if (params.status) qs.set('status', params.status);
  if (params.sort) qs.set('sort', params.sort);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return request<any[]>(`/api/tickets${q ? `?${q}` : ''}`);
}

export function fetchTicket(ticketId: number) {
  return request<any>(`/api/tickets/${ticketId}`);
}

export function fetchCohortTickets(locationId: string, date: string) {
  return request<any[]>(`/api/tickets/cohort?location_id=${encodeURIComponent(locationId)}&date=${encodeURIComponent(date)}`);
}

export function fetchTicketCounts() {
  return request<{ location_id: string; location_name: string; ticket_class_id: number; count: number }[]>(
    '/api/tickets/counts'
  );
}

export function triageTicket(ticketId: number, data: {
  ticket_class_id?: number | null;
  action_id?: number | null;
  root_cause_id?: number | null;
  status_id: number;
  comment?: string;
}) {
  return request<{ success: boolean }>(`/api/tickets/${ticketId}/triage`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// --- Nodes ---

export function fetchSparklines(macs: string[]) {
  return request<Record<string, { battery: (number | null)[]; signal: (number | null)[] }>>(
    `/api/nodes/sparklines?macs=${macs.join(',')}`
  );
}

export function fetchNodeDetail(macId: string, asOf?: string | null) {
  const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : '';
  return request<NodeDetailResponse>(`/api/nodes/${encodeURIComponent(macId)}${qs}`);
}

export function fetchNodeHistory(macId: string) {
  return request<any[]>(`/api/nodes/${encodeURIComponent(macId)}/history`);
}

export function fetchNodeTimeseries(macId: string, range?: { start: string; end: string }) {
  const qs = new URLSearchParams();
  if (range) {
    qs.set('start', range.start);
    qs.set('end', range.end);
  }
  const q = qs.toString();
  return request<{ battery: any[]; signal: any[]; dailyMetrics: any[] }>(
    `/api/nodes/${encodeURIComponent(macId)}/timeseries${q ? `?${q}` : ''}`
  );
}

export function fetchNodeTestJigReports(macId: string) {
  return request<any[]>(`/api/nodes/${encodeURIComponent(macId)}/test-jig-reports`);
}

export function fetchNodeFirmwareHistory(macId: string) {
  return request<any[]>(`/api/nodes/${encodeURIComponent(macId)}/firmware-history`);
}

export function fetchNodeInvestigation(macId: string) {
  return request<any>(`/api/nodes/${encodeURIComponent(macId)}/investigation`);
}

// --- Logs ---

export function fetchLocationLogs(locationId: string) {
  return request<any[]>(`/api/logs/location/${encodeURIComponent(locationId)}`);
}

export function addLocationLog(locationId: string, logText: string) {
  return request<any>('/api/logs/location', {
    method: 'POST',
    body: JSON.stringify({ location_id: locationId, log_text: logText }),
  });
}

export function fetchNodeLogs(nodeMacId: string) {
  return request<any[]>(`/api/logs/node/${encodeURIComponent(nodeMacId)}`);
}

export function addNodeLog(nodeMacId: string, logText: string) {
  return request<any>('/api/logs/node', {
    method: 'POST',
    body: JSON.stringify({ node_mac_id: nodeMacId, log_text: logText }),
  });
}

// --- Actions ---

export function fetchActions() {
  return request<Action[]>('/api/actions');
}

export function createAction(payload: CreateActionPayload) {
  return request<Action>('/api/actions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateAction(id: number, payload: UpdateActionPayload) {
  return request<Action>(`/api/actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function deleteAction(id: number) {
  return request<{ success: boolean }>(`/api/actions/${id}`, { method: 'DELETE' });
}

export type ActionUsage = {
  tickets: number;
  tasks: number;
  issues: number;
  can_delete: boolean;
};

export function fetchActionUsage(id: number) {
  return request<ActionUsage>(`/api/actions/${id}/usage`);
}

// --- Tasks ---

import type { Action, CreateActionPayload, UpdateActionPayload, Task, LocationTask, CreateTaskPayload, UpdateTaskPayload, Issue, IssueAlert } from '../types/triage';

export function fetchTaskCounts() {
  return request<{ location_id: string; count: number }[]>('/api/tasks/counts');
}

export function fetchTasksForLocation(locationId: string) {
  return request<LocationTask[]>(`/api/tasks/location/${locationId}`);
}

export function fetchTasksForTicket(ticketId: number) {
  return request<Task[]>(`/api/tasks/ticket/${ticketId}`);
}

export function fetchTasksForIssue(issueId: number) {
  return request<Task[]>(`/api/tasks/issue/${issueId}`);
}

export function createTask(data: CreateTaskPayload) {
  return request<Task>('/api/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateTask(taskId: string, data: UpdateTaskPayload) {
  return request<Task>(`/api/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteTask(taskId: string) {
  return request<{ success: boolean }>(`/api/tasks/${taskId}`, {
    method: 'DELETE',
  });
}

// --- Issues ---

export interface IssueListParams {
  location_id?: string;
  class_ids?: string;
  status?: string;
  stage?: 'suggested' | 'confirmed' | 'closed' | 'all';
  sort?: string;
  limit?: number;
  offset?: number;
}

export function fetchIssues(params: IssueListParams = {}) {
  const qs = new URLSearchParams();
  if (params.location_id) qs.set('location_id', params.location_id);
  if (params.class_ids) qs.set('class_ids', params.class_ids);
  if (params.status) qs.set('status', params.status);
  if (params.stage) qs.set('stage', params.stage);
  if (params.sort) qs.set('sort', params.sort);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return request<Issue[]>(`/api/issues${q ? `?${q}` : ''}`);
}

// --- Issue claim (advisory "I'm on it") ---
export type ClaimResult = {
  issue_id: number;
  claimed_by: string | null;
  claimed_at: string | null;
  stolen_from?: string | null;
};

// Claim an issue. On a live claim held by someone else the server 409s with
// { error:'already_claimed', claimed_by, claimed_at } unless steal is passed —
// the ApiError carries status 409 so the caller can prompt "take it over?".
export function claimIssue(issueId: number, steal = false) {
  return request<ClaimResult>(`/api/issues/${issueId}/claim`, {
    method: 'POST',
    body: JSON.stringify({ steal }),
  });
}

export function releaseIssue(issueId: number) {
  return request<{ issue_id: number; claimed_by: null }>(`/api/issues/${issueId}/claim`, {
    method: 'DELETE',
  });
}

// Active claims across all issues ("who's on what right now"). Server already
// TTL-filters (only live claims) and excludes closed issues.
export type ClaimedIssue = {
  issue_id: number;
  observable_id: string;
  location_id: string;
  ticket_class_id: number | null;
  priority: number | null;
  claimed_by: string;
  claimed_at: string;
  location_name: string;
  ticket_class: string | null;
  key_name: string | null;
};

export function fetchClaimedIssues() {
  return request<ClaimedIssue[]>('/api/issues/claimed');
}

// --- Location worklist (per-property scheduling) ---
export type LocationSummary = {
  location_id: string;
  location_name: string;
  // Actionable now (open, suggested, active-node, no task) — the ranking key.
  active_count: number;
  confirmed_count: number;
  p1_count: number;
  p2_count: number;
  oldest_active: string | null;
  // Property-level assignment (one ops member owns the visit). null = unassigned.
  assigned_to: string | null;
  assigned_by: string | null;
  assigned_at: string | null;
  // Real name resolved from sensorflow.users (falls back to the raw identifier).
  assigned_to_name: string | null;
};

export function fetchLocationSummary() {
  return request<LocationSummary[]>('/api/locations/summary');
}

// Assignable people — recent triage/claim actors, resolved to real names.
// `email` is the stored identifier (assigned_to); `name` is for display.
export type RosterMember = { email: string; name: string };
export function fetchRoster() {
  return request<RosterMember[]>('/api/locations/roster');
}

// Assign a property to an ops member, or pass null to unassign.
export function setLocationAssignment(locationId: string, assignedTo: string | null) {
  return request<{ location_id: string; assigned_to: string | null; assigned_by?: string | null; assigned_at?: string }>(
    `/api/locations/${locationId}/assignment`,
    { method: 'PUT', body: JSON.stringify({ assigned_to: assignedTo }) },
  );
}

export function fetchIssueCounts() {
  return request<{ location_id: string; location_name: string; ticket_class_id: number | null; stage?: 'suggested' | 'confirmed'; count: number }[]>(
    '/api/issues/counts'
  );
}

export function fetchIssue(issueId: number) {
  return request<Issue>(`/api/issues/${issueId}`);
}

export function fetchIssueAlerts(issueId: number) {
  return request<IssueAlert[]>(`/api/issues/${issueId}/alerts`);
}

// Who-did-what audit timeline for an issue. Visible to any authenticated
// operator (un-gated 2026-07-10) so peers can see an issue was already
// triaged and avoid duplicate work.
export type IssueHistoryEvent = {
  at: string;
  changed_by: string | null;
  // ticket_class_id | action_id | root_cause_id | comment | status_id
  field: string;
  // triage | reclassify | reclassify-all | status-history
  source: string;
  old_value: string | null;
  new_value: string | null;
  // Human-readable resolution of *_value for FK fields; equals *_value for
  // free-text fields (comment) and status names.
  old_display: string | null;
  new_display: string | null;
};

export function fetchIssueHistory(issueId: number) {
  return request<IssueHistoryEvent[]>(`/api/issues/${issueId}/history`);
}

export type CohortPeer = {
  observable_id: string;
  ticket_class_id: number | null;
  ticket_class: string | null;
  ticket_created: string;
  key_name: string;
  position_name: string;
  observable_name: string;
  node_type: string;
  node_sub_type: string;
  issue_id: number | null;
};
export type CohortResponse = {
  observable_id: string;
  location_id: string;
  associated_gateway: string | null;
  location_cohort: CohortPeer[];
  gateway_cohort: CohortPeer[];
};

export function fetchIssueCohort(issueId: number) {
  return request<CohortResponse>(`/api/issues/${issueId}/cohort`);
}

export type NodeExceptionEvent = {
  id: number;
  time: string;
  exception_id: number | null;
  exception_name: string | null;
  exception_description: string | null;
  file_id: number | null;
  line_number: number | null;
  rtos_task_id: number | null;
};
export type NodeExceptionsResponse = {
  macId: string;
  day: string;
  end_day: string;
  exceptions: NodeExceptionEvent[];
};

export function fetchNodeExceptions(macId: string, day: string) {
  return request<NodeExceptionsResponse>(`/api/nodes/${macId}/exceptions?day=${day}`);
}

export function triageIssue(issueId: number, data: {
  ticket_class_id?: number | null;
  action_id?: number | null;
  root_cause_id?: number | null;
  status_id?: number;
  comment?: string;
  // Label-capture metadata: filled in when this triage call is closing an
  // issue (server only persists these on close). Lets the agent measure
  // operator agreement vs override and decision latency.
  accepted_suggestion?: boolean | null;
  suggested_class_id?: number | null;
  decision_latency_ms?: number | null;
}) {
  return request<{ success: boolean; issue_id: number }>(`/api/issues/${issueId}/triage`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function reclassifyIssue(issueId: number, force = false) {
  return request<{ success: boolean; issue_id: number; classification: unknown }>(
    `/api/issues/${issueId}/reclassify`,
    {
      method: 'POST',
      body: JSON.stringify({ force }),
    }
  );
}

// reclassify-all opts:
//   force      — re-run on manually-pinned issues too
//   version    — only re-run rows pinned to this classifier_version
//   skip_llm   — server passes through to the sidecar so every prediction
//                routes through the rule-only path (no Anthropic, no
//                kNN retrieval). Useful when Anthropic is rate-limited
//                or out of credit. Voyage embeddings still run.
export interface ReclassifyJobStatus {
  id?: string;
  status: 'running' | 'done' | 'error' | 'idle';
  scanned?: number;
  updated?: number;
  errors?: number;
  auditErrors?: number;
  startedAt?: string;
  finishedAt?: string | null;
  classifier_version?: string;
  skipLlm?: boolean;
  error?: string;
}

// Kicks off the background sweep and returns immediately (202). The sweep can
// take minutes at fleet scale — poll fetchReclassifyAllStatus() for progress
// rather than awaiting a single long request (which the gateway idle-timeout
// would drop mid-flight).
export function reclassifyAllIssues(
  opts: { force?: boolean; version?: string; skip_llm?: boolean } = {},
) {
  return request<{ status: string; job: ReclassifyJobStatus }>(
    '/api/issues/reclassify-all',
    {
      method: 'POST',
      body: JSON.stringify(opts),
    }
  );
}

export function fetchReclassifyAllStatus() {
  return request<ReclassifyJobStatus>('/api/issues/reclassify-all/status');
}

export function fetchIssueSchedulerStatus() {
  return request<{ lastRunAt: string | null; lastResult: { created: number; updated: number; closed: number; errors: number } | null; running: boolean }>(
    '/api/issues/scheduler/status'
  );
}

// --- Triage Agent Health endpoints ---

export type AgentHealthSummary = {
  since_days: number;
  model: string;
  total_predictions: number;
  with_label: number;
  agree_count: number;
  override_count: number;
  holdout_total: number;
  holdout_agree: number;
  avg_confidence: number | null;
  brier_score: number | null;
};

export type AgentConfusionCell = {
  predicted_class_id: number;
  predicted_name: string;
  ground_truth_class_id: number;
  truth_name: string;
  n: number;
};

export type AgentCalibrationBucket = {
  bucket: number;
  n: number;
  agreement_rate: number;
  avg_confidence: number;
};

export type AgentTimeseriesRow = {
  metric_date: string;
  ticket_class_id: number;
  ticket_class: string;
  total_predictions: number;
  with_label: number;
  agree_count: number;
  override_count: number;
  holdout_total: number;
  holdout_agree: number;
  avg_confidence: number | null;
  brier_score: number | null;
};

export type AgentAlternative = { class_id: number; confidence: number };

export type AgentActiveLearningItem = {
  issue_id: number;
  observable_id: string;
  observable_name: string | null;
  key_name: string | null;
  /** The agent's OWN verdict (raw LLM class), not the ensemble-surfaced
   *  class. In SHADOW_MODE the surfaced class is shadowed back to the rule,
   *  so this is what "Agent says" must show to expose the disagreement. */
  agent_class_id: number;
  agent_class: string;
  agent_confidence: number;
  /** The post-ensemble class actually surfaced to operators. In shadow mode
   *  this equals the rule; compare with agent_class to detect a shadowed
   *  override. */
  surfaced_class_id: number | null;
  surfaced_class: string | null;
  decided_by: 'rule' | 'llm' | 'lgbm' | 'ensemble' | null;
  top_alternatives: AgentAlternative[];
  rule_class_id: number | null;
  rule_class: string | null;
  rule_confidence: number | null;
  reasoning: string | null;
  predicted_at: string;
  /** True when the LLM verdict differs from the rule. */
  disagreement_flag: boolean;
  /** True when rule says class 10 but agent proposed something else
   *  (which can't be promoted — informational only). */
  class10_locked: boolean;
};

export type AnalyticsFreshness = {
  latest_event: string | null;
  /** Legacy: minutes since latest_event::timestamp (always at least N
   *  minutes into UTC today). Use end_of_day_lag_minutes instead. */
  lag_minutes: number | null;
  /** Minutes since end-of-day of latest_event. <= 0 means feed is current
   *  through today. Prefer this over lag_minutes. */
  end_of_day_lag_minutes: number | null;
  row_count_7d: number;
  /** True when the analytics pool errored — render "unknown" not "broken". */
  degraded?: boolean;
};

const agentQs = (sinceDays: number, model = 'llm-retrieve') =>
  `?since_days=${sinceDays}&model=${encodeURIComponent(model)}`;

export function fetchAgentHealthSummary(sinceDays = 30) {
  return request<AgentHealthSummary>(`/api/triage-agent/health-summary${agentQs(sinceDays)}`);
}
export function fetchAgentConfusionMatrix(sinceDays = 30) {
  return request<{ cells: AgentConfusionCell[] }>(`/api/triage-agent/confusion-matrix${agentQs(sinceDays)}`);
}
export function fetchAgentCalibration(sinceDays = 30) {
  return request<{ buckets: AgentCalibrationBucket[] }>(`/api/triage-agent/calibration${agentQs(sinceDays)}`);
}
export function fetchAgentTimeseries(sinceDays = 30) {
  return request<{ rows: AgentTimeseriesRow[] }>(`/api/triage-agent/timeseries${agentQs(sinceDays)}`);
}
export function fetchAgentActiveLearningQueue(limit = 20) {
  return request<{ items: AgentActiveLearningItem[] }>(`/api/triage-agent/active-learning-queue?limit=${limit}&model=llm-retrieve`);
}
export function fetchAnalyticsFreshness() {
  return request<AnalyticsFreshness>('/api/triage-agent/analytics-freshness');
}

export type TimeToTriageRow = {
  day: string;
  n: number;
  p50_ms: number | null;
  p90_ms: number | null;
  p95_ms: number | null;
  max_ms: number | null;
};

export function fetchTimeToTriage(sinceDays = 30) {
  return request<{ since_days: number; rows: TimeToTriageRow[] }>(
    `/api/triage-agent/time-to-triage?since_days=${sinceDays}`,
  );
}

export type ClassMixWoWRow = {
  ticket_class_id: number;
  ticket_class: string;
  this_week_n: number;
  last_week_n: number;
  delta_n: number;
  delta_pct: number | null;
};

export function fetchClassMixWoW() {
  return request<{ rows: ClassMixWoWRow[] }>(`/api/triage-agent/class-mix-wow`);
}

// Rule-mix observability — used by the admin to verify the classifier post-
// reclassify. by_source = rule IDs (e.g. P4-BATTERY-REMOVED, R-STALE-AUTO-CLOSE);
// by_class = predicted class distribution; by_subclass = class-10 sub-cause
// distribution (residual when null).
export type RuleMixSourceRow = {
  predicted_class_id: number | null;
  ticket_class: string | null;
  source: string;
  n: number;
};
export type RuleMixClassRow = {
  predicted_class_id: number | null;
  ticket_class: string | null;
  decided_by: string | null;
  n: number;
};
export type RuleMixSubclassRow = {
  subclass: string;
  n: number;
};
export type RuleMixResponse = {
  window_days: number;
  by_source: RuleMixSourceRow[];
  by_class: RuleMixClassRow[];
  by_subclass: RuleMixSubclassRow[];
};

export function fetchRuleMix(days = 7) {
  return request<RuleMixResponse>(`/api/triage-agent/rule-mix?days=${days}`);
}

// Label coverage — share of issue closes carrying an operator label vs silent
// auto-close, + autoclose-rate by class. operator_pct is the headline: the
// accuracy metrics (agree-rate, brier, holdout) only see operator-labeled
// closes, so a low operator_pct means those numbers cover a biased slice.
export type LabelCoverageClassRow = {
  class_id: number | null;
  class_name: string | null;
  total: number;
  operator: number;
  auto: number;
  autoclose_rate_pct: number;
};
export type LabelCoverageResponse = {
  since_days: number;
  coverage: {
    total: number;
    operator: number;
    auto: number;
    jig: number;
    inferred: number;
    operator_pct: number;
    auto_pct: number;
  };
  by_class: LabelCoverageClassRow[];
};

export function fetchLabelCoverage(sinceDays = 30) {
  return request<LabelCoverageResponse>(
    `/api/triage-agent/label-coverage?since_days=${sinceDays}`,
  );
}

// Outcome accuracy — label-free heuristic-accuracy back-test (predicted cause vs
// what the node actually did). strong_accuracy_pct is the headline number.
export type OutcomeClassResult = {
  class_id: number | null;
  class_name: string | null;
  n: number;
  online_now: number;
  still_down: number;
  recovery_rate_pct: number;
  expectation: 'down' | 'up' | 'amb';
  correct: number | null;
  consistency_pct: number | null;
};
export type OutcomeAccuracyResponse = {
  settle_days: number;
  window_days: number;
  strong_total: number;
  strong_correct: number;
  strong_accuracy_pct: number;
  by_class: OutcomeClassResult[];
};
export function fetchOutcomeAccuracy(sinceDays = 90, settleDays = 21) {
  return request<OutcomeAccuracyResponse>(
    `/api/triage-agent/outcome-accuracy?since_days=${sinceDays}&settle_days=${settleDays}`,
  );
}

// Triage activity — are operators actually dispositioning issues, by day.
export type TriageActivityResponse = {
  since_days: number;
  totals: { operator: number; auto: number; total: number; operator_pct: number };
  operators_active: boolean;
  by_day: { day: string; operator: number; auto: number; total: number }[];
};
export function fetchTriageActivity(sinceDays = 30) {
  return request<TriageActivityResponse>(
    `/api/triage-agent/triage-activity?since_days=${sinceDays}`,
  );
}

// Heuristic drift — predicted-class mix by week + total-variation drift vs prior.
export type ClassMixTrendResponse = {
  weeks: number;
  latest_drift_pct: number | null;
  periods: {
    period: string;
    total: number;
    shares: Record<string, number>;
    drift_from_prev_pct: number | null;
  }[];
};
export function fetchClassMixTrend(weeks = 8) {
  return request<ClassMixTrendResponse>(
    `/api/triage-agent/class-mix-trend?weeks=${weeks}`,
  );
}

export type AgentConfig = {
  shadow_mode: boolean;
  sidecar_configured: boolean;
  default_model: string;
};

export function fetchAgentConfig() {
  return request<AgentConfig>('/api/triage-agent/config');
}

export type ColdClassEntry = {
  class_id: number;
  name: string;
  operator_labels: number;
  is_cold: boolean;
  to_threshold: number;
};

export type ColdClassResponse = {
  threshold: number;
  cold_classes: number[];
  last_refresh_at: string | null;
  refresh_interval_seconds: number;
  per_class: ColdClassEntry[];
};

export function fetchColdClasses() {
  return request<ColdClassResponse>('/api/triage-agent/cold-classes');
}

export type AgentIssuePrediction = {
  prediction_id: number;
  /** The class SURFACED to the operator (post-ensemble). */
  predicted_class_id: number;
  predicted_class: string | null;
  /** Confidence in the surfaced class. */
  confidence: number;
  /** Raw LLM verdict pre-ensemble. Null on rows persisted before 2026-05-18. */
  llm_class_id: number | null;
  llm_class: string | null;
  llm_confidence: number | null;
  /** Which arm of the ensemble won. Null on legacy rows. */
  decided_by: 'rule' | 'llm' | 'lgbm' | 'ensemble' | null;
  top_alternatives: AgentAlternative[];
  reasoning: string | null;
  predicted_at: string;
  is_holdout: boolean;
  disagreement_flag: boolean;
  rule_class_id: number | null;
  rule_class: string | null;
  rule_confidence: number | null;
  class10_locked: boolean;
};

export function fetchAgentIssuePrediction(issueId: number) {
  return request<{ prediction: AgentIssuePrediction | null }>(`/api/triage-agent/issue/${issueId}/prediction`);
}

export type MyContribution = {
  email: string;
  closes_today: number;
  closes_this_week: number;
  closes_total: number;
  classes_distinct: number;
};

export function fetchMyContribution() {
  return request<MyContribution>('/api/triage-agent/my-contribution');
}

export function embedBackfill(limit = 200) {
  return request<{ dispatched: number; errors: number; remaining_eligible: number; note: string }>(
    '/api/issues/embed-backfill',
    { method: 'POST', body: JSON.stringify({ limit }) },
  );
}

export function seedAgent(limit = 50) {
  return request<{ dispatched: number; errors: number; note: string }>(
    '/api/issues/seed-agent',
    { method: 'POST', body: JSON.stringify({ limit }) },
  );
}

// --- Admin usage dashboard (/usage) ---

export type UsageSummary = {
  open_total: number;
  suggested_open: number;
  confirmed_open: number;       // "ready to close": triaged, awaiting close
  p1_open: number;
  alerts_resolved_open: number; // "ready to close": alerts all resolved (sweeper-lag gauge)
};

export function fetchUsageSummary() {
  return request<UsageSummary>('/api/usage/summary');
}

export type OverridePair = {
  from_class_id: number; from_class: string | null;
  to_class_id: number; to_class: string | null; n: number;
};
export type UsageDisagreement = {
  since_days: number;
  total_labeled: number;
  overridden: number;
  override_rate_pct: number;
  by_day: { day: string; labeled: number; overridden: number }[];
  top_overrides: OverridePair[];
};

export function fetchUsageDisagreement(sinceDays = 30) {
  return request<UsageDisagreement>(`/api/usage/disagreement?since_days=${sinceDays}`);
}
